import React from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../context/LanguageContext';
import { fadeUp, stagger } from '../utils/animations';

const HERO_BG = '/athlone-1.webp';
const DRIVER_BG = '/athlone-2.webp';

export default function LandingPage({ onBook, onDrive }) {
  const { t } = useLang();
  const l = t.landing;

  const heroStyle = HERO_BG
    ? { backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : {};

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-screen flex items-center" style={heroStyle}>
        <div className={`absolute inset-0 ${HERO_BG ? 'bg-gradient-to-b from-black/65 via-black/50 to-black/80' : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'}`} />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          {/* Left - text */}
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.span variants={fadeUp} transition={{ duration: 0.3, ease: 'easeOut' }}
              className="inline-block bg-primary-600/20 backdrop-blur-sm text-primary-400 text-xs font-semibold px-3 py-1 rounded-full mb-5 border border-primary-500/30">
              {l.badge}
            </motion.span>
            <motion.h1 variants={fadeUp} transition={{ duration: 0.35, ease: 'easeOut' }}
              className="text-5xl md:text-6xl font-black text-white leading-tight mb-5">
              {l.h1a}<br />
              <span className="text-primary-400">{l.h1b}</span><br />
              {l.h1c}
            </motion.h1>
            <motion.p variants={fadeUp} transition={{ duration: 0.3, ease: 'easeOut' }}
              className="text-lg text-white/70 mb-8 leading-relaxed max-w-md">{l.subtitle}
            </motion.p>
            <motion.div variants={fadeUp} transition={{ duration: 0.3, ease: 'easeOut' }} className="flex flex-wrap gap-3">
              <motion.button
                onClick={onBook}
                className="btn-primary text-base px-7 py-3.5"
                whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(234,88,12,0.4)' }}
                whileTap={{ scale: 0.97 }}
              >
                {l.bookBtn}
              </motion.button>
              <motion.button
                onClick={onDrive}
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/60 hover:bg-white/10 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200 backdrop-blur-sm"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {l.driveBtn}
              </motion.button>
            </motion.div>
            <motion.div variants={stagger} className="mt-12 flex gap-8">
              {[['Fast', l.stat1], ['Safe', l.stat2], ['Transparent', l.stat3]].map(([val, lbl]) => (
                <motion.div key={lbl} variants={fadeUp} transition={{ duration: 0.25, ease: 'easeOut' }}>
                  <div className="text-sm font-black text-primary-400 uppercase tracking-wide">{val}</div>
                  <div className="text-xs text-white/50 mt-0.5">{lbl}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - glassmorphism ride preview */}
          <div className="hidden md:block">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 space-y-4">
              <p className="text-xs font-semibold text-white/50 uppercase tracking-wider">Live ride</p>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400 shrink-0" />
                  <span className="text-sm font-medium text-white">Airport Terminal 1</span>
                </div>
                <div className="ml-[5px] border-l-2 border-dashed border-white/20 h-4" />
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400 shrink-0" />
                  <span className="text-sm font-medium text-white">Athlone City Centre</span>
                </div>
              </div>
              <div className="bg-primary-600/20 backdrop-blur-sm rounded-2xl p-4 border border-primary-500/30 flex items-center justify-between">
                <div>
                  <div className="text-xs text-white/50">Distance</div>
                  <div className="font-bold text-white">12 km</div>
                </div>
                <div className="w-px h-8 bg-white/20" />
                <div>
                  <div className="text-xs text-white/50">ETA</div>
                  <div className="font-bold text-white">~18 min</div>
                </div>
                <div className="w-px h-8 bg-white/20" />
                <div>
                  <div className="text-xs text-white/50">Price</div>
                  <div className="font-black text-primary-400 text-xl">€24.00</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold text-white">M</div>
                <div>
                  <div className="text-sm font-semibold text-white">Driver assigned</div>
                  <div className="text-xs text-white/50">Arriving in 3 min</div>
                </div>
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold bg-green-500/20 text-green-400 px-2.5 py-1 rounded-full border border-green-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-white mb-3">{l.featuresTitle}</h2>
            <p className="text-slate-400 max-w-md mx-auto">{l.featuresSubtitle}</p>
          </div>
          <motion.div className="grid md:grid-cols-3 gap-6" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
            {l.features.map((f, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="group bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:border-primary-500/40 hover:bg-white/10 transition-all duration-300 cursor-default"
              >
                <div className="w-14 h-14 bg-primary-600/15 group-hover:bg-primary-600/25 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                  <div className={`w-5 h-5 rounded-md ${['bg-primary-500', 'bg-primary-600', 'bg-primary-700'][i]}`} />
                </div>
                <h3 className="font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block bg-primary-600/20 text-primary-400 text-xs font-semibold px-3 py-1 rounded-full mb-4 border border-primary-500/30">{l.howBadge}</span>
            <h2 className="text-3xl font-black text-white mb-8 leading-snug whitespace-pre-line">{l.howTitle}</h2>
            <div className="space-y-8">
              {l.steps.map((s, i) => (
                <div key={i} className="flex gap-5 group">
                  <div className="w-10 h-10 rounded-xl bg-primary-600 group-hover:bg-primary-700 text-white flex items-center justify-center font-black text-sm shrink-0 transition-colors duration-200">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <div className="font-bold text-white mb-1">{s.title}</div>
                    <div className="text-sm text-slate-400 leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={onBook}
              className="btn-primary mt-10 hover:scale-105 hover:shadow-lg hover:shadow-primary-600/30"
            >
              {l.bookNow}
            </button>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-4xl p-8 border border-white/20">
            <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-5">{l.priceLabel}</p>
            <div className="space-y-4">
              <div className="bg-white/10 rounded-2xl p-4 border border-white/10 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold text-sm">A</span>
                <span className="text-white text-sm font-medium">Airport Terminal 2</span>
              </div>
              <div className="text-center text-white/30 text-xl">↓</div>
              <div className="bg-white/10 rounded-2xl p-4 border border-white/10 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold text-sm">B</span>
                <span className="text-white text-sm font-medium">Athlone City Centre</span>
              </div>
            </div>
            <div className="mt-6 bg-primary-600/20 rounded-2xl p-5 border border-primary-500/30 flex items-center justify-between">
              <div><div className="text-xs text-white/50 mb-0.5">{l.distLabel}</div><div className="font-bold text-white">12.5 km</div></div>
              <div className="w-px h-10 bg-white/20" />
              <div><div className="text-xs text-white/50 mb-0.5">{l.durLabel}</div><div className="font-bold text-white">~18 min</div></div>
              <div className="w-px h-10 bg-white/20" />
              <div><div className="text-xs text-white/50 mb-0.5">{l.priceTag}</div><div className="font-black text-primary-400 text-xl">€25.00</div></div>
            </div>
            <button
              onClick={onBook}
              className="btn-primary w-full mt-4 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary-600/30"
            >
              {l.confirmBook}
            </button>
          </div>
        </div>
      </section>

      {/* Driver CTA */}
      <section
        className="relative py-20"
        style={{ backgroundImage: `url(${DRIVER_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-black text-white mb-4 leading-snug whitespace-pre-line">
              {l.driverTitle.split('\n')[0]}<br />
              <span className="text-primary-400">{l.driverTitle.split('\n')[1]}</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8 max-w-md">{l.driverDesc}</p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={onDrive}
                className="btn-primary hover:scale-105 hover:shadow-lg hover:shadow-primary-600/40"
              >
                {l.startDriving}
              </button>
              <button className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/60 hover:bg-white/10 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 active:scale-95">
                {l.learnMore}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {l.driverPerks.map(([, title, sub]) => (
              <div
                key={title}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/15 hover:border-primary-500/60 hover:bg-white/15 transition-all duration-200"
              >
                <div className="w-8 h-8 rounded-lg bg-primary-600/20 border border-primary-500/30 mb-3" />
                <div className="font-semibold text-white text-sm mb-1">{title}</div>
                <div className="text-xs text-slate-400">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
